import axios from 'axios';
import { getToken } from './keycloak';

// API Service v2.0 - Fixed metricvalues endpoint
// Normalize API URL by removing trailing dots (DNS names sometimes have trailing dots)
const normalizeApiUrl = (url) => {
  if (!url) return 'http://localhost:3001';
  url = url.trim();
  // Remove trailing dot if present
  if (url.endsWith('.')) {
    url = url.slice(0, -1);
  }
  return url;
};

const API_BASE_URL = normalizeApiUrl(process.env.REACT_APP_API_URL) || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to normalize URLs and add authentication token
api.interceptors.request.use(
  (config) => {
    // Normalize the baseURL to remove any trailing dots
    if (config.baseURL) {
      config.baseURL = normalizeApiUrl(config.baseURL);
    }
    // Also normalize the full URL if it's an absolute URL with trailing dot
    if (config.url && config.url.startsWith('http')) {
      config.url = normalizeApiUrl(config.url);
    }
    
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API Request with token:', config.url);
    } else {
      console.warn('API Request without token:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - don't reload, just reject the error
      // The component should handle this and show appropriate UI
      console.error('Authentication failed - 401 Unauthorized');
      // Don't reload here - let the components handle the error
    }
    return Promise.reject(error);
  }
);

// Generic API functions
const apiService = {
  // Generic CRUD operations
  getAll: (endpoint) => api.get(`/api/${endpoint}`),
  getById: (endpoint, id) => api.get(`/api/${endpoint}/${id}`),
  create: (endpoint, data) => {
    console.log(`API CREATE: POST /api/${endpoint}`, data);
    return api.post(`/api/${endpoint}`, data);
  },
  update: (endpoint, id, data) => api.put(`/api/${endpoint}/${id}`, data),
  delete: (endpoint, id) => api.delete(`/api/${endpoint}/${id}`),

  // Specific endpoints
  companies: {
    getAll: () => apiService.getAll('companies'),
    getById: (id) => apiService.getById('companies', id),
    create: (data) => apiService.create('companies', data),
    update: (id, data) => apiService.update('companies', id, data),
    delete: (id) => apiService.delete('companies', id),
  },

  departments: {
    getAll: () => apiService.getAll('departments'),
    getById: (id) => apiService.getById('departments', id),
    create: (data) => apiService.create('departments', data),
    update: (id, data) => apiService.update('departments', id, data),
    delete: (id) => apiService.delete('departments', id),
  },

  areas: {
    getAll: (params = {}) => api.get('/api/areas', { params }),
    getById: (id) => apiService.getById('areas', id),
    create: (data) => apiService.create('areas', data),
    update: (id, data) => apiService.update('areas', id, data),
    delete: (id) => apiService.delete('areas', id),
    getAiSummary: (areaId, forceRefresh = false) => api.get(`/api/areas/${areaId}/ai-summary`, { params: { force_refresh: forceRefresh } }),
  },

  people: {
    getAll: (params = {}) => api.get('/api/people', { params }),
    getById: (id) => apiService.getById('people', id),
    create: (data) => apiService.create('people', data),
    update: (id, data) => apiService.update('people', id, data),
    delete: (id) => apiService.delete('people', id),
  },

  metrics: {
    getAll: (params = {}) => api.get('/api/metrics', { params }),
    getById: (id) => apiService.getById('metrics', id),
    create: (data) => apiService.create('metrics', data),
    update: (id, data) => apiService.update('metrics', id, data),
    delete: (id) => apiService.delete('metrics', id),
  },

  metricValues: {
    getAll: (params = {}) => api.get('/api/metricvalues', { params }),
    getById: (id) => apiService.getById('metricvalues', id),
    create: (data) => {
      console.log('Creating metric value with endpoint: /api/metricvalues');
      return apiService.create('metricvalues', data);
    },
    update: (id, data) => apiService.update('metricvalues', id, data),
    delete: (id) => apiService.delete('metricvalues', id),
  },

  dashboards: {
    getAll: () => apiService.getAll('dashboards'),
    getById: (id) => apiService.getById('dashboards', id),
    create: (data) => apiService.create('dashboards', data),
    update: (id, data) => apiService.update('dashboards', id, data),
    delete: (id) => apiService.delete('dashboards', id),
    getMetrics: (dashboardId) => api.get(`/api/dashboards/${dashboardId}/metrics`),
    addMetric: (dashboardId, metricId) => api.post(`/api/dashboards/${dashboardId}/metrics/${metricId}`),
    removeMetric: (dashboardId, metricId) => api.delete(`/api/dashboards/${dashboardId}/metrics/${metricId}`),
    getAiSummary: (dashboardId, forceRefresh = false) => api.get(`/api/dashboards/${dashboardId}/ai-summary`, { params: { force_refresh: forceRefresh } }),
  },

  // Utility endpoints
  health: () => api.get('/api/health'),
  schema: () => api.get('/api/schema'),
  
  // AI Summary endpoint
  aiSummary: {
    get: (forceRefresh = false) => api.get('/api/ai-summary', { params: { force_refresh: forceRefresh } }),
  },
};

export default apiService;